import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Eye, Sparkles, Image, Edit3, 
  CheckCircle, AlertCircle, Gift, Users, Award,
  Settings, RefreshCw, Layers, FileText, Upload,
  Video, Globe, Calendar, Mic, Link as LinkIcon,
  ChevronRight, ChevronDown, ToggleLeft, ToggleRight
} from 'lucide-react';

export default function MockupPage() {
  const [captionText, setCaptionText] = useState(`🏠 Just Listed in Vancouver!

Welcome to 123 Oak Street - your dream home awaits!
✨ 4 spacious bedrooms
✨ 3 modern bathrooms  
✨ 2500 sqft of living space

💰 Offered at $1,250,000

📞 Call today for a private showing!

#JustListed #VancouverRealEstate #DreamHome #NewListing #RealEstateAgent`);

  const [showPreview, setShowPreview] = useState(true);
  const [propertyName, setPropertyName] = useState("123 Oak Street, Vancouver");
  const [price, setPrice] = useState("$1,250,000");
  const [features, setFeatures] = useState("4 bed, 3 bath, 2500 sqft");
  const [activeScenario, setActiveScenario] = useState("single_url");
  const [selectedTemplate, setSelectedTemplate] = useState("just_listed");

  // Calculate viral score based on caption
  const calculateViralScore = (text) => {
    let score = 50;
    if (text.includes('🏠') || text.includes('✨') || text.includes('💰')) score += 15;
    if (text.includes('#')) score += 10;
    if (text.match(/#\w+/g)?.length >= 5) score += 8;
    if (text.includes('!')) score += 5;
    if (text.includes('Call') || text.includes('Contact') || text.includes('DM')) score += 7;
    if (text.length > 100 && text.length < 500) score += 5;
    return Math.min(score, 100);
  };

  const viralScore = calculateViralScore(captionText);

  const scenarios = [
    { id: "single_url", label: "Single Post - URL Input", icon: LinkIcon },
    { id: "single_text", label: "Single Post - Text/Voice Input", icon: FileText },
    { id: "single_image", label: "Single Post - Image Upload", icon: Image },
    { id: "single_multi_image", label: "Single Post - Multiple Images", icon: Layers },
    { id: "bulk_same_same", label: "Bulk - Same Content, Same Template", icon: Calendar },
    { id: "bulk_same_diff", label: "Bulk - Same Content, Different Templates", icon: Calendar },
    { id: "bulk_diff_diff", label: "Bulk - Different Content, Different Templates", icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-viraly-dark text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2 text-viraly-muted hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
          <span className="px-4 py-2 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium">
            MOCKUP v2 - Full Scenario Coverage
          </span>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">
            <span className="gradient-text">Template Preview Feature</span>
          </h1>
          <p className="text-viraly-muted text-lg">Core Facility: Preview BEFORE Generation - All Scenarios</p>
        </div>

        {/* Key Principle */}
        <div className="card-base p-6 mb-8 bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border-orange-500/30">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-orange-400 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-lg text-orange-400 mb-2">Core Principle: Preview is INPUT to Generation</h3>
              <p className="text-viraly-muted">Customer sees preview → Confirms → THEN generation happens. No credits deducted until confirmed.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">✅ Toggle Option (not always visible)</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">✅ High Quality Preview</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">✅ Editable Caption</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">✅ Dynamic Viral Score</span>
                <span className="px-3 py-1 bg-white/10 rounded-full text-sm">✅ AI Suggestions</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scenario Selector */}
        <div className="card-base p-6 mb-8">
          <h3 className="font-bold text-lg mb-4">Select Scenario to Preview:</h3>
          <div className="grid grid-cols-4 gap-3">
            {scenarios.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveScenario(s.id)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  activeScenario === s.id 
                    ? 'border-viraly-orange bg-viraly-orange/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <s.icon className={`w-5 h-5 mb-2 ${activeScenario === s.id ? 'text-viraly-orange' : 'text-viraly-muted'}`} />
                <p className="text-sm font-medium">{s.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Main Preview Section */}
        <div className="card-base p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-viraly-gradient flex items-center justify-center font-bold">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Preview Before Generation</h2>
                <p className="text-viraly-muted text-sm">Scenario: {scenarios.find(s => s.id === activeScenario)?.label}</p>
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer bg-white/5 px-4 py-2 rounded-lg">
              {showPreview ? <ToggleRight className="w-6 h-6 text-viraly-orange" /> : <ToggleLeft className="w-6 h-6 text-viraly-muted" />}
              <span className={showPreview ? "text-viraly-orange" : "text-viraly-muted"}>Preview {showPreview ? 'On' : 'Off'}</span>
              <input 
                type="checkbox" 
                checked={showPreview}
                onChange={(e) => setShowPreview(e.target.checked)}
                className="sr-only"
              />
            </label>
          </div>

          {/* Scenario: Single Post with URL/Text/Voice */}
          {(activeScenario === "single_url" || activeScenario === "single_text") && showPreview && (
            <div className="grid grid-cols-2 gap-8">
              {/* Left: Input Summary */}
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    {activeScenario === "single_url" ? <Globe className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    Input Summary
                  </h4>
                  {activeScenario === "single_url" ? (
                    <div className="text-sm text-viraly-muted space-y-2">
                      <p><strong>URL:</strong> https://remax.ca/123-oak-street</p>
                      <p><strong>Detected:</strong> Real Estate Listing</p>
                      <p><strong>Scraped:</strong> Property details, images, price</p>
                    </div>
                  ) : (
                    <div className="text-sm text-viraly-muted space-y-2">
                      <p><strong>Input:</strong> Text / Voice transcription</p>
                      <p><strong>Topic:</strong> Property promotion</p>
                    </div>
                  )}
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Template Selected
                  </h4>
                  <select 
                    className="input-base w-full px-3 py-2 rounded-lg mb-3"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    <option value="ai_generated">🤖 AI Generated (No Template)</option>
                    <option value="just_listed">🏠 Just Listed</option>
                    <option value="price_drop">💰 Price Drop</option>
                    <option value="open_house">🏡 Open House</option>
                  </select>
                  <p className="text-xs text-viraly-muted">Change template to see preview update</p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3">What You'll Get:</h4>
                  <ul className="text-sm text-viraly-muted space-y-1">
                    <li>✅ 1 Post Image (AI Generated or from Template)</li>
                    <li>✅ Caption with hashtags</li>
                    <li>✅ Ready for: Facebook, Instagram</li>
                  </ul>
                </div>
              </div>

              {/* Right: Visual Preview */}
              <div>
                <div className="rounded-xl overflow-hidden border border-white/10 bg-gradient-to-b from-white/5 to-white/10">
                  <div className="bg-gradient-to-r from-orange-600 to-orange-800 p-4">
                    <span className="text-white font-bold text-xl">🏠 JUST LISTED</span>
                  </div>
                  <div className="bg-gradient-to-b from-gray-700 to-gray-800 h-48 flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400')] bg-cover bg-center opacity-30"></div>
                    <div className="text-center relative z-10">
                      <Sparkles className="w-12 h-12 mx-auto mb-2 text-viraly-orange" />
                      <p className="text-white font-medium">AI-Generated Premium Image</p>
                      <p className="text-gray-300 text-sm">Based on: {selectedTemplate === "ai_generated" ? "Your input" : selectedTemplate.replace("_", " ")}</p>
                    </div>
                  </div>
                  <div className="bg-black/80 p-4">
                    <p className="font-bold text-lg">{propertyName}</p>
                    <p className="text-viraly-orange font-bold text-xl">{price}</p>
                    <p className="text-gray-300">{features}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scenario: Single Image Upload */}
          {activeScenario === "single_image" && showPreview && (
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Your Uploaded Image
                  </h4>
                  <div className="h-40 bg-gradient-to-b from-gray-700 to-gray-800 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Image className="w-8 h-8 mx-auto mb-2 text-viraly-orange" />
                      <p className="text-sm text-viraly-muted">property_photo.jpg</p>
                      <p className="text-xs text-gray-500">Customer's actual image</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <h4 className="font-bold mb-2 text-blue-400">Vision AI Analysis</h4>
                  <ul className="text-sm text-viraly-muted space-y-1">
                    <li>• Detected: Modern house exterior</li>
                    <li>• Features: Pool, 2-car garage, landscaped</li>
                    <li>• Style: Contemporary architecture</li>
                    <li>• Quality: High resolution ✓</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-3">Preview with Your Image:</h4>
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <div className="bg-gradient-to-r from-orange-600 to-orange-800 p-3">
                    <span className="text-white font-bold">🏠 JUST LISTED</span>
                  </div>
                  <div className="h-48 bg-gradient-to-b from-gray-600 to-gray-700 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-white font-medium">Your Image Here</p>
                      <p className="text-gray-300 text-sm">(Actual uploaded photo)</p>
                    </div>
                  </div>
                  <div className="bg-black/80 p-4">
                    <p className="font-bold">{propertyName}</p>
                    <p className="text-viraly-orange font-bold">{price}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scenario: Multiple Images */}
          {activeScenario === "single_multi_image" && showPreview && (
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Your Uploaded Images (4)
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={`h-16 rounded-lg flex items-center justify-center ${i === 1 ? 'bg-viraly-orange/30 border-2 border-viraly-orange' : 'bg-gray-700'}`}>
                        <span className="text-xs">{i === 1 ? '★ Hero' : `Img ${i}`}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">Click image to set as hero</p>
                </div>

                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="font-bold mb-3">Collage Layout</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <button className="p-2 rounded border border-viraly-orange bg-viraly-orange/20 text-sm">Grid 2x2</button>
                    <button className="p-2 rounded border border-white/20 text-sm">Hero + 3</button>
                    <button className="p-2 rounded border border-white/20 text-sm">Carousel</button>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-3">Collage Preview:</h4>
                <div className="rounded-xl overflow-hidden border border-white/10 p-4 bg-black/50">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="h-24 bg-viraly-orange/30 rounded flex items-center justify-center">Hero</div>
                    <div className="h-24 bg-gray-700 rounded flex items-center justify-center">Img 2</div>
                    <div className="h-24 bg-gray-700 rounded flex items-center justify-center">Img 3</div>
                    <div className="h-24 bg-gray-700 rounded flex items-center justify-center">Img 4</div>
                  </div>
                  <div className="bg-black/80 p-3 rounded">
                    <p className="font-bold">{propertyName}</p>
                    <p className="text-viraly-orange">{price}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scenario: Bulk - Same Content, Same Template */}
          {activeScenario === "bulk_same_same" && showPreview && (
            <div className="space-y-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h4 className="font-bold text-green-400 mb-2">Bulk Mode: Same Content, Same Template</h4>
                <p className="text-viraly-muted text-sm">All 7 posts will use the same template and content. Only captions may vary slightly.</p>
              </div>

              <div className="grid grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map((day) => (
                  <div key={day} className="rounded-xl overflow-hidden border border-white/10 bg-white/5">
                    <div className="bg-viraly-orange/20 p-2 text-center text-sm font-medium">Day {day}</div>
                    <div className="h-24 bg-gray-700 flex items-center justify-center text-xs">Same Template</div>
                    <div className="p-2 text-xs text-viraly-muted">Same content</div>
                  </div>
                ))}
              </div>
              <p className="text-center text-viraly-muted text-sm">+2 more days with same format</p>
            </div>
          )}

          {/* Scenario: Bulk - Same Content, Different Templates */}
          {activeScenario === "bulk_same_diff" && showPreview && (
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <h4 className="font-bold text-blue-400 mb-2">Bulk Mode: Same Content, Different Templates</h4>
                <p className="text-viraly-muted text-sm">Same property details, but each day uses a different visual template.</p>
              </div>

              <div className="grid grid-cols-5 gap-3">
                {["Just Listed", "Price Focus", "Features", "Location", "Open House"].map((template, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/10 bg-white/5">
                    <div className="bg-purple-500/20 p-2 text-center text-sm font-medium">Day {i + 1}</div>
                    <div className="h-24 bg-gradient-to-b from-purple-900/50 to-gray-700 flex items-center justify-center text-xs p-2 text-center">{template}</div>
                    <div className="p-2 text-xs text-viraly-muted">123 Oak St...</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scenario: Bulk - Different Content, Different Templates */}
          {activeScenario === "bulk_diff_diff" && showPreview && (
            <div className="space-y-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h4 className="font-bold text-red-400 mb-2">Bulk Mode: Different Content, Different Templates</h4>
                <p className="text-viraly-muted text-sm">Each day has unique content AND template. Customer configures each day separately.</p>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {[
                  { day: 1, template: "Just Listed", content: "123 Oak St" },
                  { day: 2, template: "Price Drop", content: "456 Maple Ave" },
                  { day: 3, template: "Open House", content: "789 Pine Rd" },
                  { day: 4, template: "Coming Soon", content: "New Listing..." }
                ].map((item) => (
                  <div key={item.day} className="rounded-xl overflow-hidden border border-white/20 bg-white/5 cursor-pointer hover:border-viraly-orange/50 transition-colors">
                    <div className="bg-red-500/20 p-2 text-center text-sm font-medium flex items-center justify-between px-3">
                      <span>Day {item.day}</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                    <div className="h-20 bg-gradient-to-b from-red-900/30 to-gray-700 flex items-center justify-center text-xs p-2 text-center">{item.template}</div>
                    <div className="p-2 text-xs text-viraly-muted truncate">{item.content}</div>
                  </div>
                ))}
              </div>
              <p className="text-center text-viraly-muted text-sm">Click any day to edit its content and template</p>
            </div>
          )}

          {/* Caption Preview - Always Show */}
          {showPreview && (
            <div className="mt-8 bg-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-viraly-orange" />
                  Caption Preview (Editable)
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-viraly-muted">Viral Score:</span>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                    viralScore >= 80 ? 'bg-green-500/20' : viralScore >= 60 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                  }`}>
                    <span className={`font-bold ${
                      viralScore >= 80 ? 'text-green-400' : viralScore >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{viralScore}/100</span>
                    {viralScore >= 80 && <span>🔥</span>}
                  </div>
                  <button className="px-3 py-1 bg-viraly-orange/20 text-viraly-orange rounded-lg text-sm hover:bg-viraly-orange/30 transition-colors flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> AI Suggest
                  </button>
                </div>
              </div>
              <textarea 
                className="input-base w-full p-4 h-40 resize-none rounded-lg"
                value={captionText}
                onChange={(e) => setCaptionText(e.target.value)}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-viraly-muted">💡 Tip: Posts with emojis and 5+ hashtags perform 23% better</p>
                <p className={`text-xs ${viralScore >= 80 ? 'text-green-400' : viralScore >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {viralScore >= 80 ? '✨ Great caption!' : viralScore >= 60 ? '👍 Good, could be better' : '⚠️ Needs improvement'}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex items-center justify-between">
            <button className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Edit
            </button>
            <div className="flex items-center gap-4">
              <p className="text-viraly-muted">Credits needed: <span className="text-white font-bold">1 post</span></p>
              <button className="btn-primary px-8 py-3 rounded-xl flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600">
                Confirm & Generate <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Section: Loyalty & Referral (Collapsed) */}
        <details className="card-base mb-8">
          <summary className="p-6 cursor-pointer font-bold text-lg flex items-center gap-3">
            <Award className="w-6 h-6 text-viraly-orange" />
            Loyalty & Referral Program (Click to Expand)
          </summary>
          <div className="p-6 pt-0 space-y-6">
            {/* Loyalty Tiers */}
            <div>
              <h3 className="font-bold mb-4">Rolling 12-Month Loyalty Tiers</h3>
              <div className="grid grid-cols-5 gap-4">
                {[
                  { tier: "Bronze", discount: 5, threshold: 100, color: "amber" },
                  { tier: "Silver", discount: 10, threshold: 300, color: "gray" },
                  { tier: "Gold", discount: 15, threshold: 750, color: "yellow" },
                  { tier: "Platinum", discount: 20, threshold: 1500, color: "purple" },
                  { tier: "Diamond", discount: 25, threshold: 3000, color: "cyan" }
                ].map((t) => (
                  <div key={t.tier} className={`p-4 rounded-xl bg-${t.color}-500/10 border border-${t.color}-500/30 text-center`}>
                    <p className={`text-${t.color}-400 font-bold mb-1`}>{t.tier}</p>
                    <p className="text-2xl font-bold">{t.discount}%</p>
                    <p className="text-xs text-viraly-muted mt-1">After ${t.threshold}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Referral */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h4 className="text-green-400 font-bold mb-3">New Customer (Referee)</h4>
                <p className="text-2xl font-bold">10% <span className="text-sm font-normal text-viraly-muted">off first purchase</span></p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <h4 className="text-blue-400 font-bold mb-3">Referrer Rewards</h4>
                <p className="text-viraly-muted">50 points + 5 free posts per referral</p>
              </div>
            </div>
          </div>
        </details>

        {/* Backend APIs */}
        <div className="card-base p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
            <h3 className="font-bold text-lg">Backend APIs Required (BUILD FIRST)</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-white/5 rounded-lg p-3">
              <p className="font-bold text-viraly-orange mb-2">Preview APIs</p>
              <ul className="text-viraly-muted space-y-1">
                <li>🔨 GET /templates/{"{id}"}/layout</li>
                <li>🔨 POST /preview/generate</li>
                <li>🔨 POST /captions/score</li>
                <li>🔨 POST /captions/suggest</li>
              </ul>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <p className="font-bold text-viraly-orange mb-2">Loyalty APIs</p>
              <ul className="text-viraly-muted space-y-1">
                <li>🔨 GET /user/loyalty-status</li>
                <li>🔨 PUT /admin/loyalty-settings</li>
                <li>🔨 POST /admin/recalculate</li>
              </ul>
            </div>
            <div className="bg-white/5 rounded-lg p-3">
              <p className="font-bold text-viraly-orange mb-2">Referral APIs</p>
              <ul className="text-viraly-muted space-y-1">
                <li>🔨 GET /admin/referral-settings</li>
                <li>🔨 PUT /admin/referral-settings</li>
                <li>🔨 POST /referral/apply</li>
              </ul>
            </div>
          </div>
        </div>

        <p className="text-center text-viraly-muted">Mockup v2 - March 30, 2026 | All scenarios covered | Awaiting approval</p>
      </div>
    </div>
  );
}
